import React, { useState } from 'react';
import { LogOut, Menu, X } from 'lucide-react';
import { useAuth } from '../../hooks/useAuth';
import { useApp } from '../../context/AppContext';
import { MENU_SECTIONS, VISTA_A_MODULO } from './menuConfig';
import { chatsService } from '../../services/api';
import usePolling from '../../hooks/usePolling';

const INTERVALO_PENDIENTES_MS = 60 * 1000;

const Sidebar = () => {
  const { user, logout } = useAuth();
  const { currentView, handleViewChange, setSidebarOpen } = useApp();
  const [isOpen, setIsOpen] = useState(false);

  const handleNavClick = (view) => {
    handleViewChange(view);
    setIsOpen(false);
    setSidebarOpen(false);
  };

  const puedeVer = (itemId) => {
    if (!user?.permisos) return true; // permisos aun no resueltos -- no ocultar de mas
    const modulo = VISTA_A_MODULO[itemId];
    if (!modulo) return true;
    // req.user.permisos es el mapa { [modulo]: { puede_ver, ... } } que
    // devuelve fn_permisos_efectivos (migracion 031) via User.getPermisos.
    return !!user.permisos[modulo]?.puede_ver;
  };

  const puedeVerChats = !user?.permisos || !!user.permisos.chats?.puede_ver;
  const [chatsPendientes, setChatsPendientes] = useState(0);
  usePolling(async () => {
    if (!puedeVerChats) return;
    const res = await chatsService.pendientes();
    setChatsPendientes(res.data?.pendientes || 0);
  }, INTERVALO_PENDIENTES_MS);

  const seccionesVisibles = MENU_SECTIONS
    .map((seccion) => ({
      ...seccion,
      items: seccion.items.filter((item) => puedeVer(item.id)),
    }))
    .filter((seccion) => seccion.items.length > 0);

  return (
    <>
      {/* Botón hamburguesa para móvil */}
      <button
        onClick={() => setIsOpen(!isOpen)}
        className="lg:hidden fixed top-5 left-5 z-50 bg-[#111827] text-gray-100 p-3 rounded-xl shadow-lg border border-gray-800 hover:bg-[#1c2534] transition-colors duration-150"
      >
        {isOpen ? <X size={22} strokeWidth={2.5} /> : <Menu size={22} strokeWidth={2.5} />}
      </button>

      {/* Overlay para cerrar el menú en móvil */}
      {isOpen && (
        <div
          className="lg:hidden fixed inset-0 bg-black/50 z-30 transition-opacity duration-200"
          onClick={() => setIsOpen(false)}
        />
      )}

      {/* Sidebar */}
      <div className={`
        fixed lg:static inset-y-0 left-0 z-40
        bg-[#111827] text-gray-100 w-68 min-h-screen flex flex-col
        transform transition-transform duration-200 ease-out
        border-r border-white/5
        ${isOpen ? 'translate-x-0' : '-translate-x-full lg:translate-x-0'}
      `}>
        {/* Header */}
        <div className="flex items-center gap-3 px-5 py-5 border-b border-white/5">
          <div className="bg-[#33475b] w-9 h-9 rounded-lg flex items-center justify-center shrink-0">
            <span className="text-gray-100 text-sm font-bold">G</span>
          </div>
          <div className="flex flex-col min-w-0">
            <h1 className="font-semibold text-[15px] text-gray-100 leading-tight">Gaman ERP</h1>
            <p className="text-[11px] text-gray-500 leading-tight truncate">Electrosoluciones Gaman</p>
          </div>
        </div>

        {/* Navegación */}
        <nav className="flex-1 overflow-y-auto px-3 py-4 flex flex-col gap-4">
          {seccionesVisibles.map((seccion) => (
            <div key={seccion.label} className="flex flex-col gap-0.5">
              <div className="px-2.5 pb-1.5 text-[10.5px] font-bold tracking-wider text-gray-600 uppercase">
                {seccion.label}
              </div>
              {seccion.items.map((item) => {
                const activo = currentView === item.id;
                return (
                  <button
                    key={item.id}
                    onClick={() => handleNavClick(item.id)}
                    className={`w-full flex items-center gap-3 py-2.5 rounded-lg transition-colors duration-150 ${
                      activo
                        ? 'bg-[#1c2534] text-gray-100 font-semibold pl-2.5 pr-3 border-l-[3px] border-[#6b8ba4]'
                        : 'text-gray-400 hover:bg-white/5 hover:text-gray-200 pl-3 pr-3 border-l-[3px] border-transparent'
                    }`}
                  >
                    <item.icon size={18} strokeWidth={activo ? 2.2 : 1.8} className="shrink-0" />
                    <span className="text-[13.5px]">{item.label}</span>
                    {item.id === 'chats' && chatsPendientes > 0 && (
                      <span
                        className="ml-auto rounded-full bg-red-600 text-white text-[11px] font-semibold px-2 py-0.5"
                        aria-label={`${chatsPendientes} chats esperan a una persona`}
                      >
                        {chatsPendientes}
                      </span>
                    )}
                  </button>
                );
              })}
            </div>
          ))}
        </nav>

        {/* Usuario y logout */}
        <div className="px-3 pb-4 pt-2 border-t border-white/5">
          <div className="flex items-center gap-2.5 p-2.5 rounded-lg bg-white/[0.03] mt-3">
            <div className="w-8 h-8 rounded-full bg-[#33475b] flex items-center justify-center shrink-0">
              <span className="text-gray-100 text-xs font-bold">
                {(user?.nombre || 'U').slice(0, 2).toUpperCase()}
              </span>
            </div>
            <div className="flex flex-col min-w-0 flex-1">
              <p className="text-[12.5px] font-semibold text-gray-200 truncate leading-tight">{user?.nombre || 'Usuario'}</p>
              <p className="text-[11px] text-gray-500 truncate leading-tight">{user?.rol || 'Rol'}</p>
            </div>
            <button
              onClick={logout}
              aria-label="Cerrar sesión"
              className="text-gray-500 hover:text-gray-200 p-1 transition-colors duration-150"
            >
              <LogOut size={16} strokeWidth={1.8} />
            </button>
          </div>
        </div>
      </div>
    </>
  );
};

export default Sidebar;
